/**
 * The marketplace's Sell counter — everything the player bought, offered back.
 *
 * ## Why this is a tab and not a screen
 *
 * It used to be one: the Locker (`/inventory`), a page that listed four catalogues, equipped
 * from all of them, and sold back. Equipping moved to Loadout, where every other kind of
 * equipping already lived, and what was left was "the things you already bought, with a price
 * on them" — which is the marketplace looking the other way, not a destination of its own.
 * `design/gdd/meta-screens.md` records the call.
 *
 * ## What counts as sellable
 *
 * Only a thing with a purchase behind it. A skin reached at level 12, a default weapon, a title
 * earned at a rank — none of those have a `CurrencyLedger` DEBIT row, so `SellbackService`
 * refuses them and this never offers them. Deciding it here as well means no dead button and
 * no round trip to discover a refusal, but the server is still the one that decides: every
 * sale asks for a fresh quote first and shows the server's own numbers.
 *
 * ## Why the tile shows what was PAID
 *
 * The payout is half of what THAT purchase actually cost, recovered from the account's own
 * ledger — so it is not knowable without asking, and asking once per tile would be forty
 * requests to draw a grid. The tile shows the price the player paid, the confirmation shows
 * the real quote, and the sale happens after that.
 */
import { button, confirmAction, emptyState, icon, plateSelect } from '../ui';
import { formatNumber, t } from '../../i18n';
import { api } from '../../../core/network/api';
import {
  getSellbackQuotes,
  sellItemsBack,
  type BulkQuote,
  type SellbackCurrency,
} from '../sellback-api';
import { fetchCharmsCatalogue } from '../charms-api';
import { fetchSpraysCatalogue } from '../sprays-api';
import { fetchEmotesCatalogue } from '../emotes-api';
import { fetchPlayerCardsCatalogue } from '../player-cards-api';
import { fetchWeaponSkinsCatalogue } from '../weapon-skins-api';
import { refreshPlayerProgress } from '../player-progress-store';
import { skinStillPath, stillImg } from './renders';
import { priceChip, tintDot, type MarketPrice } from './marketplace-tile';
import type { MarketSection } from './marketplace-section';

/** One owned, bought thing — whatever catalogue it came out of. */
interface OwnedItem {
  /** The `UserItem` id the sell-back endpoints key on: `skin:3`, `wskin:10003`, `charm:4`… */
  itemId: string;
  name: string;
  description: string;
  price: MarketPrice;
  art: HTMLElement;
  /** Human-readable category, for the row and for grouping a sort. Derived from the id's own
   * prefix rather than stored twice — the prefix is already the thing that decides sellability
   * server-side, so there is one source for "what kind of thing is this". */
  kind: string;
}

const KIND_BY_PREFIX: Record<string, string> = {
  'skin:': 'Operator',
  'wskin:': 'Finish',
  'title:': 'Title',
  'charm:': 'Charm',
  'spray:': 'Spray',
  'emote:': 'Callout',
  'card:': 'Card',
};

function kindOf(itemId: string): string {
  for (const [prefix, label] of Object.entries(KIND_BY_PREFIX)) {
    if (itemId.startsWith(prefix)) return label;
  }
  return 'Item';
}

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

/** A round tint, for the categories whose art is a tint rather than a picture. */
function chip(colour: string): HTMLElement {
  return tintDot(colour);
}

/**
 * Everything bought, across every catalogue, in one pass.
 *
 * Each source is caught on its own: one dead catalogue costs its own items and leaves the rest
 * sellable, the same posture every other marketplace category takes. A source that fails is
 * indistinguishable here from a source with nothing bought in it, and that is correct — both
 * mean "nothing of this kind to sell right now".
 */
async function loadOwned(): Promise<OwnedItem[]> {
  const [skins, loadout, titles, charms, sprays, emotes, cards] = await Promise.all([
    api
      .get<{ skins: Array<{ id: number; name: string; description: string; unlock: { kind: string; gemCost?: number } ; owned: boolean }> }>('/cosmetics')
      .catch(() => null),
    fetchWeaponSkinsCatalogue().catch(() => null),
    api
      .get<{ titles: Array<{ slug: string; name: string; description: string; owned: boolean; unlock: { kind: string; softCost?: number } }> }>('/game/titles')
      .catch(() => null),
    fetchCharmsCatalogue().catch(() => null),
    fetchSpraysCatalogue().catch(() => null),
    fetchEmotesCatalogue().catch(() => null),
    fetchPlayerCardsCatalogue().catch(() => null),
  ]);

  const out: OwnedItem[] = [];

  for (const skin of skins?.skins ?? []) {
    // Only a BOUGHT skin has a purchase to refund; a level or rank unlock has no ledger row.
    if (!skin.owned || skin.unlock.kind !== 'purchase') continue;
    out.push({
      itemId: `skin:${skin.id}`,
      name: skin.name,
      description: skin.description,
      price: { amount: skin.unlock.gemCost ?? 0, currency: 'PREMIUM' },
      kind: kindOf(`skin:${skin.id}`),
      art: stillImg({ src: skinStillPath(skin.id), alt: '', width: 384, height: 512, className: 'h-full w-auto object-contain' }),
    });
  }

  for (const finish of loadout?.weaponSkins ?? []) {
    // Every owned finish came through a real debit — unlike a skin there is no granted case.
    if (!finish.owned) continue;
    out.push({
      itemId: `wskin:${finish.id}`,
      name: finish.name,
      description: '',
      price: { amount: finish.priceCoins, currency: finish.currency },
      kind: kindOf(`wskin:${finish.id}`),
      art: chip(finish.tint),
    });
  }

  for (const title of titles?.titles ?? []) {
    if (!title.owned || title.unlock.kind !== 'purchase') continue;
    out.push({
      itemId: `title:${title.slug}`,
      name: title.name,
      description: title.description ?? '',
      price: { amount: title.unlock.softCost ?? 0, currency: 'SOFT' },
      kind: kindOf(`title:${title.slug}`),
      art: chip('#8a8f96'),
    });
  }

  for (const charm of charms?.charms ?? []) {
    if (!charm.owned) continue;
    out.push({
      itemId: `charm:${charm.id}`,
      name: charm.name,
      description: charm.description,
      price: { amount: charm.priceCoins, currency: charm.currency },
      kind: kindOf(`charm:${charm.id}`),
      art: chip(hex(charm.tint)),
    });
  }

  for (const spray of sprays?.sprays ?? []) {
    if (!spray.owned) continue;
    out.push({
      itemId: `spray:${spray.id}`,
      name: spray.name,
      description: spray.description,
      price: { amount: spray.priceCoins, currency: spray.currency },
      kind: kindOf(`spray:${spray.id}`),
      art: chip(hex(spray.tint)),
    });
  }

  for (const emote of emotes?.emotes ?? []) {
    if (!emote.owned) continue;
    out.push({
      itemId: `emote:${emote.id}`,
      name: emote.name,
      description: `“${emote.line}”`,
      price: { amount: emote.priceCoins, currency: emote.currency },
      kind: kindOf(`emote:${emote.id}`),
      art: chip('#17e0c9'),
    });
  }

  for (const card of cards?.cards ?? []) {
    if (!card.owned) continue;
    out.push({
      itemId: `card:${card.id}`,
      name: card.name,
      description: card.description,
      price: { amount: card.priceCoins, currency: card.currency },
      kind: kindOf(`card:${card.id}`),
      art: chip(hex(card.frame)),
    });
  }

  return out;
}

type SortKey = 'payout' | 'paid' | 'name' | 'kind';

/** A row, once its quote has landed. `quote` is null while the bulk call is in flight and for
 * an item the server says cannot be sold — the two are told apart by `reason`. */
interface SellRow {
  item: OwnedItem;
  paidAmount: number;
  payoutAmount: number;
  rateBps: number;
  currency: SellbackCurrency;
  /** Why this one cannot be sold, or null. A granted skin still appears — hidden, a player just
   * wonders where it went; shown with a reason, they learn the rule once. */
  reason: string | null;
}

/** Per-currency totals. Credits and Gems are never summed: the server never converts between
 * them, so a single number would be a lie about what lands in the wallet. */
interface Totals { SOFT: number; PREMIUM: number }

function emptyTotals(): Totals {
  return { SOFT: 0, PREMIUM: 0 };
}

function moneyRow(label: string, totals: Totals, slot: string, bold = false): HTMLElement {
  const row = document.createElement('div');
  row.className = `kit-well flex items-center justify-between gap-4 px-3 py-1.5 text-[12px] ${bold ? 'text-text' : 'text-text-dim'}`;
  row.dataset.sell = slot;
  const name = document.createElement('span');
  name.textContent = label;
  const value = document.createElement('span');
  value.className = `inline-flex items-center gap-3 tabular-nums ${bold ? 'kit-label font-heading text-[14px]' : ''}`;
  // Both currencies always, even at zero: a row that appears and disappears as a selection
  // changes makes the panel jump, and "0 gems" is information when you expected some.
  for (const currency of ['SOFT', 'PREMIUM'] as const) {
    const part = document.createElement('span');
    part.className = 'inline-flex items-center gap-1';
    part.dataset.currency = currency;
    part.append(
      icon(currency === 'PREMIUM' ? 'premium' : 'credits', { size: 20 }),
      document.createTextNode(formatNumber(totals[currency])),
    );
    value.append(part);
  }
  row.append(name, value);
  return row;
}

export function createSellSection(): MarketSection {
  let items: OwnedItem[] = [];
  let rows: SellRow[] = [];
  let ready = false;
  let busy = false;
  let feedback: string | null = null;
  let disposed = false;
  let sortKey: SortKey = 'payout';
  /** Selection survives a re-sort and a repaint, and is keyed by item id rather than by index
   * for exactly that reason. Cleared only by a sale or a reload. */
  const selected = new Set<string>();
  const subscribers = new Set<() => void>();

  function notify(): void {
    if (disposed) return;
    for (const cb of subscribers) cb();
  }

  async function load(): Promise<void> {
    try {
      items = await loadOwned();
    } catch {
      items = [];
    }
    // One request for every payout on the page. A payout is a ledger lookup per item, so the
    // single-item route would be one request per row to draw one screen — and the page's whole
    // point is showing the real number rather than the price on the tile.
    rows = [];
    selected.clear();
    if (items.length > 0) {
      const byId = new Map(items.map((item) => [item.itemId, item]));
      let quotes: BulkQuote[] = [];
      try {
        quotes = (await getSellbackQuotes(items.map((item) => item.itemId))).quotes;
      } catch (error) {
        // Left empty: every row then shows its paid price with no payout and no checkbox, which
        // is honest — the server did not tell us what it would pay, so nothing is offered. And
        // the player is TOLD, rather than shown a page that quietly offers nothing: a silent
        // "you own nothing sellable" is indistinguishable from the real thing.
        quotes = [];
        feedback = (error as Error).message;
      }
      const seen = new Set<string>();
      for (const quote of quotes) {
        const item = byId.get(quote.itemId);
        if (!item) continue;
        seen.add(quote.itemId);
        rows.push(
          quote.ok
            ? {
                item,
                paidAmount: quote.paidAmount,
                payoutAmount: quote.payoutAmount,
                rateBps: quote.rateBps,
                currency: quote.paidCurrency,
                reason: null,
              }
            : {
                item,
                paidAmount: item.price.amount,
                payoutAmount: 0,
                rateBps: 0,
                currency: item.price.currency,
                reason: quote.reason,
              },
        );
      }
      // An item the quote call never mentioned is still listed, unsellable and unexplained,
      // rather than silently dropped — a thing you own vanishing from the page you own it on is
      // worse than a row you cannot act on.
      //
      // `reason` is ALWAYS set here, and the `quotes.length === 0 ? null : …` it used to read
      // was the whole bug behind "продажа не работает" (2026-09-21). `null` means sellable, so
      // a quote call that failed outright — which is exactly what happened once an account
      // owned more than the route's hundred-id cap — turned every row into an offer with a
      // payout of zero. The page then sent a sale the server refused line by line. A row with
      // no quote is a row that cannot be sold, whether one quote is missing or all of them are.
      for (const item of items) {
        if (seen.has(item.itemId)) continue;
        rows.push({
          item,
          paidAmount: item.price.amount,
          payoutAmount: 0,
          rateBps: 0,
          currency: item.price.currency,
          reason: 'no quote',
        });
      }
    }
    if (disposed) return;
    ready = true;
    busy = false;
    notify();
  }

  void load();


  function sortedRows(): SellRow[] {
    const out = [...rows];
    out.sort((a, b) => {
      switch (sortKey) {
        // Highest payout first — the default, because the question this page answers is "what
        // is worth selling", and the answer is at the top.
        case 'payout': return b.payoutAmount - a.payoutAmount || a.item.name.localeCompare(b.item.name);
        case 'paid': return b.paidAmount - a.paidAmount || a.item.name.localeCompare(b.item.name);
        case 'kind': return a.item.kind.localeCompare(b.item.kind) || a.item.name.localeCompare(b.item.name);
        default: return a.item.name.localeCompare(b.item.name);
      }
    });
    return out;
  }

  const sellable = (): SellRow[] => rows.filter((row) => row.reason === null);

  function totalsFor(chosen: readonly SellRow[]): { paid: Totals; payout: Totals; fee: Totals } {
    const paid = emptyTotals();
    const payout = emptyTotals();
    const fee = emptyTotals();
    for (const row of chosen) {
      paid[row.currency] += row.paidAmount;
      payout[row.currency] += row.payoutAmount;
      // The fee is what the house keeps, derived rather than stated: the server sends the rate
      // and the payout, and `paid - payout` is the only figure that cannot disagree with them.
      fee[row.currency] += row.paidAmount - row.payoutAmount;
    }
    return { paid, payout, fee };
  }

  /** Sells everything ticked, in one request, and reports per item. */
  async function sellSelected(): Promise<void> {
    if (busy) return;
    const chosen = sellable().filter((row) => selected.has(row.item.itemId));
    if (chosen.length === 0) return;
    const { paid, payout, fee } = totalsFor(chosen);
    const rate = chosen[0]!.rateBps;

    const confirmed = await confirmAction({
      title: t('inventory.sell.confirmTitle'),
      body: t('inventory.sell.bulkBody', { count: chosen.length }),
      extra: [
        moneyRow(t('inventory.sell.paidLabel'), paid, 'paid'),
        moneyRow(t('inventory.sell.feeLabel', { rate: ((10000 - rate) / 100).toFixed(0) }), fee, 'fee'),
        moneyRow(t('inventory.sell.payoutLabel'), payout, 'payout', true),
      ],
      okLabel: t('inventory.sell.confirmOk'),
      tone: 'danger',
    });
    if (!confirmed) return;

    busy = true;
    feedback = null;
    notify();
    try {
      const res = await sellItemsBack(chosen.map((row) => row.item.itemId));
      const failed = res.results.filter((r) => !r.ok);
      // Partial success is a real outcome and is reported as one: some of these sold and paid
      // out, and a bare "failed" would tell the player their items are gone for nothing.
      if (failed.length > 0) {
        feedback = t('inventory.sell.partial', { failed: failed.length, sold: res.results.length - failed.length });
      }
      void refreshPlayerProgress();
      await load();
    } catch (error) {
      if (disposed) return;
      feedback = (error as Error).message;
      busy = false;
      notify();
    }
  }

  /**
   * The Sell pane.
   *
   * Its own markup rather than the shared tile grid, because it answers a different question.
   * A catalogue grid is built to make you want one thing; this is a ledger of what you already
   * own, and it has to show the arithmetic — what you paid, what the house keeps, what lands —
   * and let you act on several rows at once ("где рассчет продажи с игровых налогов и сортировка
   * и мульти селект продажи" — Arturs, 2026-09-21). None of that fits a buy-tile.
   */
  function renderPane(container: HTMLElement): void {
    if (!ready) {
      const loading = document.createElement('p');
      loading.className = 'kit-well px-3 py-2 text-[13px] text-text-dim';
      loading.textContent = t('common.loading');
      container.append(loading);
      return;
    }
    if (rows.length === 0) {
      const empty = emptyState({ art: 'crate', title: t('inventory.sell.empty'), hint: '' });
      empty.dataset.sell = 'empty';
      container.append(empty);
      return;
    }

    const visible = sortedRows();
    const chosen = sellable().filter((row) => selected.has(row.item.itemId));

    // ── toolbar: sort, select-all ───────────────────────────────────────────────
    const bar = document.createElement('div');
    bar.className = 'flex flex-wrap items-center gap-3 mb-3';

    const all = document.createElement('label');
    all.className = 'inline-flex items-center gap-2 kit-label font-heading uppercase text-[12px] min-h-[44px] cursor-pointer';
    const allBox = document.createElement('input');
    allBox.type = 'checkbox';
    allBox.dataset.sell = 'select-all';
    allBox.className = 'w-4 h-4 accent-cyan';
    allBox.checked = chosen.length > 0 && chosen.length === sellable().length;
    allBox.addEventListener('change', () => {
      selected.clear();
      if (allBox.checked) for (const row of sellable()) selected.add(row.item.itemId);
      notify();
    });
    all.append(allBox, document.createTextNode(t('inventory.sell.selectAll')));

    const sort = document.createElement('select');
    sort.dataset.sell = 'sort';
    for (const [key, label] of [
      ['payout', t('inventory.sell.sortPayout')],
      ['paid', t('inventory.sell.sortPaid')],
      ['name', t('inventory.sell.sortName')],
      ['kind', t('inventory.sell.sortKind')],
    ] as const) {
      const option = document.createElement('option');
      option.value = key;
      option.textContent = label;
      option.selected = sortKey === key;
      sort.append(option);
    }
    sort.addEventListener('change', () => {
      sortKey = sort.value as SortKey;
      notify();
    });

    const sortPick = plateSelect(sort);
    sortPick.className = 'relative w-48';
    bar.append(all, sortPick);
    container.append(bar);

    // ── rows ────────────────────────────────────────────────────────────────────
    const list = document.createElement('div');
    list.dataset.sell = 'rows';
    list.className = 'flex flex-col gap-2';
    for (const row of visible) list.append(sellRow(row));
    container.append(list);

    // ── the arithmetic, for whatever is ticked ──────────────────────────────────
    const { paid, payout, fee } = totalsFor(chosen);
    const rate = chosen[0]?.rateBps ?? rows.find((r) => r.reason === null)?.rateBps ?? 0;
    const summary = document.createElement('div');
    summary.dataset.sell = 'summary';
    summary.className = 'kit-plate mt-4 flex flex-col gap-1.5';
    summary.append(
      moneyRow(t('inventory.sell.paidLabel'), paid, 'total-paid'),
      moneyRow(t('inventory.sell.feeLabel', { rate: ((10000 - rate) / 100).toFixed(0) }), fee, 'total-fee'),
      moneyRow(t('inventory.sell.payoutLabel'), payout, 'total-payout', true),
    );
    const action = button(t('inventory.sell.sellSelected', { count: chosen.length }), {
      variant: 'danger',
      className: 'w-full min-h-[44px] mt-2',
      disabled: busy || chosen.length === 0,
      onClick: () => void sellSelected(),
    });
    summary.append(action);
    container.append(summary);
  }

  function sellRow(row: SellRow): HTMLElement {
    const el = document.createElement('label');
    el.dataset.sell = `row-${row.item.itemId}`;
    const canSell = row.reason === null;
    el.className = [
      'kit-plate [--kp-rim:12px] [--kp-pad:10px] flex flex-wrap items-center gap-3 min-h-[44px]',
      canSell ? 'kit-plate-hit' : 'grayscale-[0.6] brightness-75',
      selected.has(row.item.itemId) ? 'kit-selected' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'w-5 h-5 shrink-0 accent-cyan';
    box.dataset.sell = 'pick';
    box.checked = selected.has(row.item.itemId);
    // An unsellable row keeps its checkbox, disabled, rather than losing it: a missing control
    // reads as a layout bug, a disabled one plus the reason beside it reads as a rule.
    box.disabled = !canSell;
    box.addEventListener('change', () => {
      if (box.checked) selected.add(row.item.itemId);
      else selected.delete(row.item.itemId);
      notify();
    });

    // The item's render in its own well, as large as the row allows.
    const art = document.createElement('span');
    art.className = 'kit-well relative shrink-0 w-14 h-14 inline-flex items-center justify-center overflow-hidden';
    art.append(row.item.art);

    const name = document.createElement('span');
    name.className = 'flex flex-col min-w-0 flex-1 basis-32';
    const title = document.createElement('span');
    title.className = 'kit-label font-heading uppercase text-[14px] truncate';
    title.textContent = row.item.name;
    const sub = document.createElement('span');
    sub.className = 'text-[11px] text-text-dim truncate';
    sub.textContent = row.reason ? `${row.item.kind} · ${row.reason}` : row.item.kind;
    name.append(title, sub);

    // Paid (struck through) then what lands — the figures the summary totals, on every row, so
    // the total is checkable rather than trusted.
    const figures = document.createElement('span');
    figures.className = 'shrink-0 flex items-center gap-3 tabular-nums text-[12px]';
    const paidEl = document.createElement('span');
    paidEl.dataset.sell = 'row-paid';
    paidEl.className = 'text-text-dim line-through';
    paidEl.textContent = formatNumber(row.paidAmount);
    const payoutEl = priceChip(row.currency, formatNumber(row.payoutAmount));
    payoutEl.dataset.sell = 'row-payout';
    figures.append(paidEl, payoutEl);

    el.append(box, art, name, figures);
    return el;
  }

  return {
    id: 'sell',
    label: t('shop.tabs.sell'),
    note: t('shop.sell.note'),
    renderPane,
    // The pane draws itself; nothing asks this for tiles any more, and an empty list is the
    // honest answer for a category that no longer speaks in tiles.
    getItems: () => [],
    isReady: () => ready,
    // Never hides its own tab. An empty Sell tab is a true and useful answer — "you have
    // bought nothing yet" — where a hidden one would look like the feature does not exist.
    hasFailed: () => false,
    getFeedback: () => feedback,
    subscribe(cb) {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    dispose() {
      disposed = true;
      subscribers.clear();
    },
  };
}
